// eslint-disable-next-line import/no-cycle
import devTipsService from '@/services/devTipsService';

/**
 * Prefer NestJS / class-validator messages (`response.data.message`) over axios defaults.
 * @param {unknown} error
 * @param {string} fallback
 * @returns {string}
 */
function getApiErrorMessage(error, fallback) {
  const payloadMessage = error?.response?.data?.message;
  if (typeof payloadMessage === 'string' && payloadMessage.trim()) {
    return payloadMessage.trim();
  }
  if (Array.isArray(payloadMessage) && payloadMessage.length) {
    return payloadMessage.map((entry) => String(entry)).filter(Boolean).join('\n');
  }
  if (typeof error?.message === 'string' && error.message.trim()) {
    return error.message.trim();
  }
  return fallback;
}

// State
const state = {
  tips: [],
  currentTip: null,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
  filters: {
    dimensionValue: '',
    active: null,
    search: '',
    dimensionStatus: '',
  },
  statistics: {
    total: 0,
    active: 0,
    inactive: 0,
    byDimension: {},
  },
  selectedTips: [],
};

// Mutations
const mutations = {
  SET_LOADING(currentState, loading) {
    // eslint-disable-next-line no-param-reassign
    currentState.loading = loading;
  },

  SET_ERROR(currentState, error) {
    // eslint-disable-next-line no-param-reassign
    currentState.error = error;
  },

  SET_TIPS(currentState, tips) {
    // eslint-disable-next-line no-param-reassign
    currentState.tips = tips;
  },

  SET_CURRENT_TIP(currentState, tip) {
    // eslint-disable-next-line no-param-reassign
    currentState.currentTip = tip;
  },

  ADD_TIP(currentState, tip) {
    currentState.tips.unshift(tip);
  },

  UPDATE_TIP(currentState, updatedTip) {
    const index = currentState.tips.findIndex((tip) => tip._id === updatedTip._id);
    if (index !== -1) {
      currentState.tips.splice(index, 1, updatedTip);
    }
  },

  REMOVE_TIP(currentState, tipId) {
    // eslint-disable-next-line no-param-reassign
    currentState.tips = currentState.tips.filter((tip) => tip._id !== tipId);
  },

  SET_PAGINATION(currentState, pagination) {
    // eslint-disable-next-line no-param-reassign
    currentState.pagination = { ...currentState.pagination, ...pagination };
  },

  SET_FILTERS(currentState, filters) {
    // eslint-disable-next-line no-param-reassign
    currentState.filters = { ...currentState.filters, ...filters };
  },

  SET_STATISTICS(currentState, statistics) {
    // eslint-disable-next-line no-param-reassign
    currentState.statistics = statistics;
  },

  SET_SELECTED_TIPS(currentState, selectedTips) {
    // eslint-disable-next-line no-param-reassign
    currentState.selectedTips = selectedTips;
  },

  TOGGLE_TIP_SELECTION(currentState, tipId) {
    const index = currentState.selectedTips.indexOf(tipId);
    if (index === -1) {
      currentState.selectedTips.push(tipId);
    } else {
      currentState.selectedTips.splice(index, 1);
    }
  },

  CLEAR_SELECTED_TIPS(currentState) {
    // eslint-disable-next-line no-param-reassign
    currentState.selectedTips = [];
  },
};

// Actions
const actions = {
  /**
   * Load all development tips with optional filtering and pagination
   */
  async loadTips({ commit, state: currentState, dispatch }, params = {}) {
    try {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);

      const queryParams = {
        ...currentState.filters,
        ...params,
        page: params.page || currentState.pagination.page,
        limit: params.limit || currentState.pagination.limit,
      };

      // If no specific page is requested and we want all tips, load them with pagination
      if (!params.page && !params.limit && !params.specificPage) {
        try {
          return await dispatch('loadAllTipsWithPagination', queryParams);
        } catch (error) {
          console.warn('Pagination loading failed, falling back to single request:', error);
          // Fallback to single request with max limit
          const fallbackParams = { ...queryParams, limit: 100 };
          return await devTipsService.getAllTips(fallbackParams);
        }
      }

      const response = await devTipsService.getAllTips(queryParams);

      commit('SET_TIPS', response.tips);
      commit('SET_PAGINATION', {
        page: response.page,
        limit: response.limit,
        total: response.total,
        totalPages: response.totalPages,
      });

      return response;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to load development tips');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Load all development tips using pagination to bypass backend limit
   */
  async loadAllTipsWithPagination({ commit }, baseParams = {}) {
    try {
      let allTips = [];
      const backendLimit = 100; // Backend hard limit

      // First, get the first page to determine total pages
      const firstResponse = await devTipsService.getAllTips({
        ...baseParams,
        page: 1,
        limit: backendLimit,
      });

      if (firstResponse.tips && firstResponse.tips.length > 0) {
        allTips = allTips.concat(firstResponse.tips);

        // Calculate total pages needed
        const totalPages = firstResponse.totalPages
         || Math.ceil(firstResponse.total / backendLimit);

        // If there are more pages, fetch them
        if (totalPages > 1) {
          const remainingPages = [];
          for (let page = 2; page <= totalPages; page += 1) {
            remainingPages.push(
              devTipsService.getAllTips({
                ...baseParams,
                page,
                limit: backendLimit,
              }),
            );
          }

          // Wait for all remaining responses
          const remainingResults = await Promise.all(remainingPages);

          // Process remaining responses
          remainingResults.forEach((response) => {
            if (response.tips && response.tips.length > 0) {
              allTips = allTips.concat(response.tips);
            }
          });
        }
      }

      commit('SET_TIPS', allTips);
      commit('SET_PAGINATION', {
        page: 1,
        limit: allTips.length,
        total: allTips.length,
        totalPages: 1,
      });

      return {
        tips: allTips,
        total: allTips.length,
        page: 1,
        limit: allTips.length,
        totalPages: 1,
      };
    } catch (error) {
      console.error('Error loading all tips with pagination:', error);
      throw error;
    }
  },

  /**
   * Manually load all development tips using pagination
   */
  async loadAllTips({ commit, dispatch }) {
    try {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);

      const result = await dispatch('loadAllTipsWithPagination', {});

      commit('SET_LOADING', false);
      return result;
    } catch (error) {
      commit('SET_LOADING', false);
      console.error('Error in loadAllTips:', error);
      throw error;
    }
  },

  /**
   * Simple method to test store functionality
   */
  async testStore({ commit }) {
    try {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);

      commit('SET_LOADING', false);
      return { success: true, message: 'Store is working' };
    } catch (error) {
      commit('SET_LOADING', false);
      commit('SET_ERROR', error.message);
      throw error;
    }
  },

  /**
   * Load a specific development tip by ID
   */
  async loadTipById({ commit }, id) {
    try {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);

      const tip = await devTipsService.getTipById(id);
      commit('SET_CURRENT_TIP', tip);

      return tip;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to load development tip');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Create a new development tip
   */
  async createTip({ commit, dispatch }, tipData) {
    try {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);

      const newTip = await devTipsService.createTip(tipData);
      commit('ADD_TIP', newTip);

      // Show success notification
      dispatch('notifications/showToast', {
        severity: 'success',
        summary: 'Success',
        detail: 'Development tip created successfully',
        life: 3000,
      }, { root: true });

      return newTip;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to create development tip');

      // Show error notification
      dispatch('notifications/showToast', {
        severity: 'error',
        summary: 'Error',
        detail: error.message || 'Failed to create development tip',
        life: 5000,
      }, { root: true });

      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Update a development tip
   */
  async updateTip({ commit, dispatch }, { id, tipData }) {
    try {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);

      const updatedTip = await devTipsService.updateTip(id, tipData);
      commit('UPDATE_TIP', updatedTip);

      // Show success notification
      dispatch('notifications/showToast', {
        severity: 'success',
        summary: 'Success',
        detail: 'Development tip updated successfully',
        life: 3000,
      }, { root: true });

      return updatedTip;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to update development tip');

      // Show error notification
      dispatch('notifications/showToast', {
        severity: 'error',
        summary: 'Error',
        detail: error.message || 'Failed to update development tip',
        life: 5000,
      }, { root: true });

      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Toggle tip status
   */
  async toggleTipStatus({ commit, dispatch }, { id, active }) {
    try {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);

      const updatedTip = await devTipsService.toggleTipStatus(id, active);
      commit('UPDATE_TIP', updatedTip);

      // Show success notification
      dispatch('notifications/showToast', {
        severity: 'success',
        summary: 'Success',
        detail: `Development tip ${active ? 'activated' : 'deactivated'} successfully`,
        life: 3000,
      }, { root: true });

      return updatedTip;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to toggle tip status');

      // Show error notification
      dispatch('notifications/showToast', {
        severity: 'error',
        summary: 'Error',
        detail: error.message || 'Failed to toggle tip status',
        life: 5000,
      }, { root: true });

      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Delete a development tip
   */
  async deleteTip({ commit, dispatch }, id) {
    try {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);

      await devTipsService.deleteTip(id);
      commit('REMOVE_TIP', id);

      // Show success notification
      dispatch('notifications/showToast', {
        severity: 'success',
        summary: 'Success',
        detail: 'Development tip deleted successfully',
        life: 3000,
      }, { root: true });
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to delete development tip');

      // Show error notification
      dispatch('notifications/showToast', {
        severity: 'error',
        summary: 'Error',
        detail: error.message || 'Failed to delete development tip',
        life: 5000,
      }, { root: true });

      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Bulk import development tips
   */
  async bulkImport({ commit, dispatch }, bulkData) {
    try {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);

      const result = await devTipsService.bulkImport(bulkData);

      const errorLines = Array.isArray(result.errors) ? result.errors.filter(Boolean) : [];
      const errorPreview = errorLines.length
        ? `\n${errorLines.slice(0, 5).join('\n')}${
          errorLines.length > 5 ? `\n…and ${errorLines.length - 5} more` : ''
        }`
        : '';

      // Show result notification (include per-row messages for FA-05.19)
      dispatch('notifications/showToast', {
        severity: result.failureCount === 0 ? 'success' : 'warn',
        summary: 'Bulk Import Result',
        detail: `Successfully imported ${result.successCount} tips${
          result.failureCount > 0 ? `, ${result.failureCount} failed` : ''
        }${errorPreview}`,
        life: result.failureCount > 0 ? 8000 : 5000,
      }, { root: true });

      // Reload tips to show new ones (loadTips clears error — set after)
      if (result.successCount > 0) {
        await dispatch('loadTips');
      }

      if (result.failureCount > 0 && errorLines.length) {
        commit('SET_ERROR', errorLines.join('\n'));
      }

      return result;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to bulk import tips');

      dispatch('notifications/showToast', {
        severity: 'error',
        summary: 'Error',
        detail: error.message || 'Failed to bulk import tips',
        life: 5000,
      }, { root: true });

      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Chunked bulk import development tips
   */
  async chunkedBulkImport({ commit }, chunkedBulkData) {
    try {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);

      const result = await devTipsService.chunkedBulkImport(chunkedBulkData);

      return result;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to chunked bulk import tips');

      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Bulk delete development tips
   */
  async bulkDelete({ commit, dispatch }, tipIds) {
    try {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);

      const result = await devTipsService.bulkDelete({ tipIds });

      // Show result notification
      dispatch('notifications/showToast', {
        severity: result.failureCount === 0 ? 'success' : 'warn',
        summary: 'Bulk Delete Result',
        detail: `Successfully deleted ${result.successCount} tips${
          result.failureCount > 0 ? `, ${result.failureCount} failed` : ''
        }`,
        life: 5000,
      }, { root: true });

      // Clear selection and reload
      commit('CLEAR_SELECTED_TIPS');
      if (result.successCount > 0) {
        await dispatch('loadTips');
      }

      return result;
    } catch (error) {
      const detail = getApiErrorMessage(error, 'Failed to bulk delete tips');
      commit('SET_ERROR', detail);

      dispatch('notifications/showToast', {
        severity: 'error',
        summary: 'Error',
        detail,
        life: 5000,
      }, { root: true });

      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Resolve tip ids that match the current filters across every page (backend
   * page size is capped at 100). Used for "Select all matching filters".
   */
  async fetchFilteredTipIds({ state: currentState, rootGetters }) {
    const backendLimit = 100;
    const baseParams = { ...currentState.filters };
    const first = await devTipsService.getAllTips({
      ...baseParams,
      page: 1,
      limit: backendLimit,
    });
    let tips = [...(first.tips || [])];
    const totalPages = first.totalPages
      || Math.ceil((first.total || 0) / backendLimit)
      || 1;

    if (totalPages > 1) {
      const pages = [];
      for (let page = 2; page <= totalPages; page += 1) {
        pages.push(devTipsService.getAllTips({
          ...baseParams,
          page,
          limit: backendLimit,
        }));
      }
      const rest = await Promise.all(pages);
      rest.forEach((response) => {
        if (response.tips?.length) tips = tips.concat(response.tips);
      });
    }

    // dimensionStatus valid/invalid is only fully known on the client (backend
    // returns any tip with a non-empty dimensionValue for both).
    const status = currentState.filters.dimensionStatus;
    if (status === 'valid' || status === 'invalid') {
      const dimensions = rootGetters['dimensions/getAllDimensions'] || [];
      if (dimensions.length > 0) {
        const isValid = (value) => dimensions.some((dim) => dim.value === value);
        tips = tips.filter((tip) => {
          const hasValue = !!(tip.dimensionValue && String(tip.dimensionValue).trim());
          if (!hasValue) return false;
          const valid = isValid(tip.dimensionValue);
          return status === 'valid' ? valid : !valid;
        });
      }
    }

    return tips.map((tip) => tip._id).filter(Boolean);
  },

  /**
   * Set active status for many tips (FA-05.18 Sammel-Statuswechsel).
   */
  async bulkToggleStatus({ commit, dispatch }, { tipIds, active }) {
    try {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);

      const result = await devTipsService.bulkToggleStatus({ tipIds, active });

      dispatch('notifications/showToast', {
        severity: result.failureCount === 0 ? 'success' : 'warn',
        summary: 'Bulk Status Result',
        detail: `Successfully ${active ? 'activated' : 'deactivated'} ${result.successCount} tips${
          result.failureCount > 0 ? `, ${result.failureCount} failed` : ''
        }`,
        life: 5000,
      }, { root: true });

      commit('CLEAR_SELECTED_TIPS');
      if (result.successCount > 0) {
        await dispatch('loadTips');
      }

      return result;
    } catch (error) {
      const detail = getApiErrorMessage(error, 'Failed to bulk toggle tip status');
      commit('SET_ERROR', detail);

      dispatch('notifications/showToast', {
        severity: 'error',
        summary: 'Error',
        detail,
        life: 5000,
      }, { root: true });

      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Load statistics
   */
  async loadStatistics({ commit }) {
    try {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);

      const statistics = await devTipsService.getStatistics();
      commit('SET_STATISTICS', statistics);

      return statistics;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to load statistics');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Update filters and reload
   */
  async updateFilters({ commit, dispatch }, filters) {
    commit('SET_FILTERS', filters);
    commit('SET_PAGINATION', { page: 1 }); // Reset to first page
    await dispatch('loadTips');
  },

  /**
   * Clear filters and reload
   */
  async clearFilters({ commit, dispatch }) {
    commit('SET_FILTERS', {
      dimensionValue: '',
      active: null,
      search: '',
      dimensionStatus: '',
    });
    commit('SET_PAGINATION', { page: 1 });
    await dispatch('loadTips');
  },

  // ===== USER-SPECIFIC DEVTIPS ACTIONS =====

  /**
   * Get user's DevTip library
   */
  async getUserDevTipLibrary({ commit }) {
    try {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);

      const response = await devTipsService.getUserDevTipLibrary();

      // Update the current tip if we have one
      if (response.data && response.data.length > 0) {
        commit('SET_CURRENT_TIP', response.data[0]);
      }

      return response;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to load user DevTip library');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Mark a DevTip as viewed by the user
   */
  async markDevTipAsViewed({ commit, dispatch }, tipId) {
    try {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);

      const response = await devTipsService.markDevTipAsViewed(tipId);

      // Refresh the user's DevTip library to get updated status
      await dispatch('getUserDevTipLibrary');

      return response;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to mark DevTip as viewed');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Submit feedback for a DevTip
   */
  async submitDevTipFeedback({ commit, dispatch }, feedbackData) {
    try {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);

      const response = await devTipsService.submitDevTipFeedback(feedbackData);

      // Refresh the user's DevTip library to get updated status
      await dispatch('getUserDevTipLibrary');

      return response;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to submit DevTip feedback');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },
};

// Getters
const getters = {
  /**
   * Get all tips
   */
  allTips: (currentState) => currentState.tips,

  /**
   * Get current tip
   */
  currentTip: (currentState) => currentState.currentTip,

  /**
   * Get loading state
   */
  isLoading: (currentState) => currentState.loading,

  /**
   * Get error state
   */
  error: (currentState) => currentState.error,

  /**
   * Get pagination info
   */
  pagination: (currentState) => currentState.pagination,

  /**
   * Get current filters
   */
  filters: (currentState) => currentState.filters,

  /**
   * Get statistics
   */
  statistics: (currentState) => currentState.statistics,

  /**
   * Get selected tips
   */
  selectedTips: (currentState) => currentState.selectedTips,

  /**
   * Check if any tips are selected
   */
  hasSelectedTips: (currentState) => currentState.selectedTips.length > 0,

  /**
   * Get selected tips count
   */
  selectedTipsCount: (currentState) => currentState.selectedTips.length,

  /**
   * Get tips by dimension
   */
  tipsByDimension: (currentState) => (dimensionValue) => currentState.tips
    .filter((tip) => tip.dimensionValue === dimensionValue),

  /**
   * Active catalog tips for one dimension (FA-05.27 coverage counts).
   */
  activeTipsByDimension: (currentState) => (dimensionValue) => currentState.tips
    .filter((tip) => tip.dimensionValue === dimensionValue && tip.active),

  /**
   * Get active tips
   */
  activeTips: (currentState) => currentState.tips.filter((tip) => tip.active),

  /**
   * Get inactive tips
   */
  inactiveTips: (currentState) => currentState.tips.filter((tip) => !tip.active),
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
